import { Navigate, useLocation, type RouteObject } from "react-router";

import { LoadingBlock } from "./components/States";
import { DarOnly } from "./layout/Guards";
import { useCurrentUser } from "./lib/currentUser";
import { englishUrl } from "./lib/legacyUrls";
import { RootLayout } from "./layout/RootLayout";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { CraPage } from "./pages/CraPage";
import { MyRequestsPage } from "./pages/MyRequestsPage";
import { MyTeamPage } from "./pages/MyTeamPage";
import { NotFoundPage, RouteErrorPage } from "./pages/NotFoundPage";
import { PersonFollowUpPage } from "./pages/PersonFollowUpPage";
import { ProjectDetailPage } from "./pages/project/ProjectDetailPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { TeamReportsPage } from "./pages/TeamReportsPage";

/** Legacy French URLs (sent emails, bookmarks) land on their English equivalent; anything else is a 404. */
function FallbackPage() {
  const { pathname, search, hash } = useLocation();
  const target = englishUrl(pathname, search);
  return target ? <Navigate to={target + hash} replace /> : <NotFoundPage />;
}

/** The DAR lands on its dashboard, everyone else on the projects. */
function HomeRedirect() {
  const me = useCurrentUser();
  return <Navigate to={me.is_dar ? "/dashboard" : "/projects"} replace />;
}

/** Administration screens are split out of the main bundle: only the DAR ever loads them. */
function lazyPage(load: () => Promise<{ default: React.ComponentType }>): RouteObject["lazy"] {
  return async () => ({ Component: (await load()).default });
}

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    // Shown while a lazy (admin) route loads on the very first navigation.
    hydrateFallbackElement: (
      <div className="ada-full-screen">
        <LoadingBlock label="Chargement…" />
      </div>
    ),
    children: [
      {
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: "projects", element: <ProjectsPage /> },
          { path: "projects/:slug", element: <ProjectDetailPage /> },
          { path: "my-requests", element: <MyRequestsPage /> },
          { path: "reports", element: <CraPage /> },
          { path: "reports/team", element: <TeamReportsPage /> },
          { path: "integrations", element: <IntegrationsPage /> },
          { path: "people/:id", element: <PersonFollowUpPage /> },
          { path: "my-team", element: <MyTeamPage /> },
          {
            element: <DarOnly />,
            children: [
              { path: "dashboard", lazy: lazyPage(() => import("./pages/admin/DashboardPage")) },
              { path: "ipc", lazy: lazyPage(() => import("./pages/admin/IpcPlanningPage")) },
              { path: "ipc/minutes", lazy: lazyPage(() => import("./pages/admin/IpcMinutesPage")) },
              { path: "resources", lazy: lazyPage(() => import("./pages/admin/ResourcesPage")) },
            ],
          },
          {
            path: "admin",
            element: <DarOnly />,
            children: [
              { index: true, element: <Navigate to="/admin/requests" replace /> },
              { path: "requests", lazy: lazyPage(() => import("./pages/admin/AdminRequestsPage")) },
              { path: "projects", lazy: lazyPage(() => import("./pages/admin/AdminProjectsPage")) },
              { path: "people", lazy: lazyPage(() => import("./pages/admin/AdminPeoplePage")) },
              { path: "reports", lazy: lazyPage(() => import("./pages/admin/AdminCompliancePage")) },
              { path: "settings", lazy: lazyPage(() => import("./pages/admin/AdminSettingsPage")) },
            ],
          },
          { path: "*", element: <FallbackPage /> },
        ],
      },
    ],
  },
];
